import { AutomationForgeElementType } from "@redotech/redo-content-builder-common/forge/marketing-automation-forge-types";
import { useTranslation } from "@redotech/redo-i18n";
import AlertHexagonIcon from "@redotech/redo-icons/alert-hexagon.svg";
import AlertOctagonIcon from "@redotech/redo-icons/alert-octagon.svg";
import DownLeftIcon from "@redotech/redo-icons/arrow-narrow-down-left_filled.svg";
import UpRightIcon from "@redotech/redo-icons/arrow-narrow-up-right_filled.svg";
import SuccessCheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle_filled.svg";
import ClipboardIcon from "@redotech/redo-icons/clipboard-check_filled.svg";
import ClockIcon from "@redotech/redo-icons/clock-stopwatch.svg";
import CodeIcon from "@redotech/redo-icons/code-01_filled.svg";
import DuplicateIcon from "@redotech/redo-icons/copy-01.svg";
import FileIcon from "@redotech/redo-icons/file-02.svg";
import {
  default as BranchIcon,
  default as GitBranchIcon,
} from "@redotech/redo-icons/git-branch-01.svg";
import HelpCircleIcon from "@redotech/redo-icons/help-circle.svg";
import MailIcon from "@redotech/redo-icons/mail-01.svg";
import MessageIcon from "@redotech/redo-icons/message-check-square.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import PlayCircle from "@redotech/redo-icons/play-circle.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-ccw-01_filled.svg";
import ScalesIcon from "@redotech/redo-icons/scales-01.svg";
import ShoppingCartIcon from "@redotech/redo-icons/shopping-cart-03.svg";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import UsersIcon from "@redotech/redo-icons/users-01.svg";
import XCircleIcon from "@redotech/redo-icons/x-circle.svg";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { DraggableFormElement } from "@redotech/redo-web/form-builder/draggable-form-element";
import { FormElementDrag } from "@redotech/redo-web/form-builder/form-element-drag";
import { FormSidePanel } from "@redotech/redo-web/form-builder/form-side-panel";
import { Text } from "@redotech/redo-web/text";
import { BgColorValue } from "@redotech/redo-web/theme/box";
import { memo, ReactElement } from "react";
import * as flowEditorStyles from "./flow-editor.module.css";
import { InvalidFlowStep } from "./steps";

export const AutomationForgeDisplayInfo: Record<
  AutomationForgeElementType,
  { Icon: ReactElement; name: string }
> = {
  [AutomationForgeElementType.SEND_EMAIL]: {
    Icon: <RedoFeaturedIcon color="success" Icon={MailIcon} />,
    name: "Send email",
  },
  [AutomationForgeElementType.SEND_SMS]: {
    Icon: <RedoFeaturedIcon color="success" Icon={MessageIcon} />,
    name: "Send SMS",
  },
  [AutomationForgeElementType.SEND_WEBHOOK]: {
    Icon: <RedoFeaturedIcon color="success" Icon={CodeIcon} />,
    name: "Send webhook",
  },
  [AutomationForgeElementType.MANAGE_CUSTOMER_TAGS]: {
    Icon: <RedoFeaturedIcon color="success" Icon={TagIcon} />,
    name: "Manage customer tags",
  },
  [AutomationForgeElementType.MANAGE_STATIC_SEGMENT]: {
    Icon: <RedoFeaturedIcon color="success" Icon={UsersIcon} />,
    name: "Manage static segments",
  },
  [AutomationForgeElementType.RECOVER]: {
    Icon: <RedoFeaturedIcon color="success" Icon={RefreshIcon} />,
    name: "Recover",
  },
  [AutomationForgeElementType.SHOPPER_AGENT_MESSAGE]: {
    Icon: <RedoFeaturedIcon color="success" Icon={MessageIcon} />,
    name: "Start Agent Conversation",
  },
  [AutomationForgeElementType.END]: {
    Icon: <RedoFeaturedIcon color="black" Icon={AlertOctagonIcon} />,
    name: "End",
  },
  [AutomationForgeElementType.WAIT]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={ClockIcon} />,
    name: "Wait",
  },
  [AutomationForgeElementType.WAIT_FOR_EVENT]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={ClockIcon} />,
    name: "Wait for event",
  },
  [AutomationForgeElementType.BRANCH]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={BranchIcon} />,
    name: "Branch",
  },
  [AutomationForgeElementType.UPSELL]: {
    Icon: <RedoFeaturedIcon color="success" Icon={UpRightIcon} />,
    name: "Upsell",
  },
  [AutomationForgeElementType.DOWNSELL]: {
    Icon: <RedoFeaturedIcon color="success" Icon={DownLeftIcon} />,
    name: "Downsell",
  },
  [AutomationForgeElementType.OMS_ACTION]: {
    Icon: <RedoFeaturedIcon color="success" Icon={PlayCircle} />,
    name: "Action",
  },
  [AutomationForgeElementType.OMS_CONDITION]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={BranchIcon} />,
    name: "Condition",
  },
  [AutomationForgeElementType.OMS_CODE]: {
    Icon: <RedoFeaturedIcon color="success" Icon={CodeIcon} />,
    name: "Code",
  },
  [AutomationForgeElementType.RCO_PARCEL_CONDITION]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={BranchIcon} />,
    name: "Condition",
  },
  [AutomationForgeElementType.RCO_PARCEL]: {
    Icon: <RedoFeaturedIcon color="gray" Icon={PackageIcon} />,
    name: "Package",
  },
  [AutomationForgeElementType.CHECKOUT_EXPERIENCE]: {
    Icon: <RedoFeaturedIcon color="gray" Icon={ShoppingCartIcon} />,
    name: "Checkout experience",
  },
  [AutomationForgeElementType.BLOCK_CHECKOUT]: {
    Icon: <RedoFeaturedIcon color="error" Icon={AlertHexagonIcon} />,
    name: "Block checkout",
  },
  [AutomationForgeElementType.RCO_SEGMENT]: {
    Icon: <RedoFeaturedIcon color="success" Icon={BranchIcon} />,
    name: "Segment",
  },
  [AutomationForgeElementType.RCO_SPLIT]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={ScalesIcon} />,
    name: "A/B Test",
  },
  [AutomationForgeElementType.AB_TEST]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={DuplicateIcon} />,
    name: "Weighted branch",
  },
  [AutomationForgeElementType.POST_PURCHASE_PAGE]: {
    Icon: <RedoFeaturedIcon color="gray" Icon={ShoppingCartIcon} />,
    name: "Post-purchase page",
  },
  [AutomationForgeElementType.POST_PURCHASE_SEGMENT]: {
    Icon: <RedoFeaturedIcon color="success" Icon={BranchIcon} />,
    name: "Post-purchase segment",
  },
  [AutomationForgeElementType.POST_PURCHASE_SPLIT]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={ScalesIcon} />,
    name: "A/B Test",
  },
  [AutomationForgeElementType.TRACKING_PAGE]: {
    Icon: <RedoFeaturedIcon color="success" Icon={ShoppingCartIcon} />,
    name: "Tracking page",
  },
  [AutomationForgeElementType.RULE_ACTIONS]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={DuplicateIcon} />,
    name: "Actions",
  },
  [AutomationForgeElementType.RULE_CONDITIONS]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={GitBranchIcon} />,
    name: "Conditions",
  },
  [AutomationForgeElementType.RULE_ACTIONS_V3]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={DuplicateIcon} />,
    name: "Actions",
  },
  [AutomationForgeElementType.RULE_CONDITIONS_V3]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={GitBranchIcon} />,
    name: "Conditions",
  },
  [AutomationForgeElementType.WORK_ORDER_WORK_STEP]: {
    Icon: <RedoFeaturedIcon color="success" Icon={ClipboardIcon} />,
    name: "Work step",
  },
  [AutomationForgeElementType.WORK_ORDER_CONDITION]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={GitBranchIcon} />,
    name: "Condition",
  },
  [AutomationForgeElementType.SUPPLIES_SHIPPING_CART_VALUE]: {
    Icon: <RedoFeaturedIcon color="gray" Icon={GitBranchIcon} />,
    name: "Cart value",
  },
  [AutomationForgeElementType.SUPPLIES_SHIPPING_FIRST_TIME_CUSTOMER]: {
    Icon: <RedoFeaturedIcon color="gray" Icon={GitBranchIcon} />,
    name: "First supplies order",
  },
  [AutomationForgeElementType.SUPPLIES_SHIPPING_AUTOMATED_REORDER]: {
    Icon: <RedoFeaturedIcon color="gray" Icon={GitBranchIcon} />,
    name: "Automated reorder",
  },
  [AutomationForgeElementType.SUPPLIES_SHIPPING_MERCHANT]: {
    Icon: <RedoFeaturedIcon color="gray" Icon={GitBranchIcon} />,
    name: "Merchant",
  },
  [AutomationForgeElementType.SUPPLIES_SHIPPING_CART_ITEMS]: {
    Icon: <RedoFeaturedIcon color="gray" Icon={GitBranchIcon} />,
    name: "Cart items",
  },
  [AutomationForgeElementType.SUPPLIES_SHIPPING_FAMILY_TAGS]: {
    Icon: <RedoFeaturedIcon color="gray" Icon={GitBranchIcon} />,
    name: "Product family tags",
  },
  [AutomationForgeElementType.SUPPLIES_SHIPPING_DESTINATION_PROVINCE]: {
    Icon: <RedoFeaturedIcon color="gray" Icon={GitBranchIcon} />,
    name: "Destination state",
  },
  [AutomationForgeElementType.SUPPLIES_SHIPPING_RATE]: {
    Icon: <RedoFeaturedIcon color="success" Icon={TruckIcon} />,
    name: "Set shipping cost",
  },
  [AutomationForgeElementType.PROCESS_RETURN]: {
    Icon: <RedoFeaturedIcon color="success" Icon={CheckCircleIcon} />,
    name: "Process return",
  },
  [AutomationForgeElementType.CONCIERGE_BLOCK]: {
    Icon: <RedoFeaturedIcon color="success" Icon={MessageIcon} />,
    name: "Concierge",
  },
  [AutomationForgeElementType.HSA_QUESTION_GROUP]: {
    Icon: <RedoFeaturedIcon color="brand" Icon={HelpCircleIcon} />,
    name: "Question group",
  },
  [AutomationForgeElementType.HSA_CONDITION]: {
    Icon: <RedoFeaturedIcon color="warning" Icon={GitBranchIcon} />,
    name: "Condition",
  },
  [AutomationForgeElementType.HSA_KNOCKOUT]: {
    Icon: <RedoFeaturedIcon color="error" Icon={XCircleIcon} />,
    name: "Knockout",
  },
  [AutomationForgeElementType.HSA_CONTENT]: {
    Icon: <RedoFeaturedIcon color="gray" Icon={FileIcon} />,
    name: "Content screen",
  },
  [AutomationForgeElementType.HSA_END]: {
    Icon: <RedoFeaturedIcon color="success" Icon={SuccessCheckCircleIcon} />,
    name: "Survey complete",
  },
};

export interface StepSection {
  name?: string;
  steps: AutomationForgeElementType[];
}

export interface AutomationForgeElementTypeWithDisabledTooltip {
  kind: AutomationForgeElementType;
  disabledTooltip?: string;
}

export const FlowEditorLeftPanel = memo(function AutomationsLeftPanel({
  title,
  description,
  stepOptions,
  disabledStepTypes,
  invalidSteps,
  onGoToStep,
  renderInvalidStepAlert,
  bgColor,
}: {
  title?: string | undefined;
  description?: string | undefined;
  stepOptions: StepSection[];
  disabledStepTypes?: AutomationForgeElementTypeWithDisabledTooltip[];
  invalidSteps?: InvalidFlowStep[];
  onGoToStep?: (id: string) => void;
  renderInvalidStepAlert?: (
    invalidSteps: InvalidFlowStep[] | undefined,
    stepTypes: AutomationForgeElementType[],
    onGoToStep?: (id: string) => void,
  ) => React.ReactNode;
  bgColor?: BgColorValue;
}) {
  const { t } = useTranslation();
  const translatedNames: Record<AutomationForgeElementType, string> = {
    [AutomationForgeElementType.SEND_EMAIL]: t("Send email"),
    [AutomationForgeElementType.SEND_SMS]: t("Send SMS"),
    [AutomationForgeElementType.SEND_WEBHOOK]: t("Send webhook"),
    [AutomationForgeElementType.MANAGE_CUSTOMER_TAGS]: t(
      "Manage customer tags",
    ),
    [AutomationForgeElementType.MANAGE_STATIC_SEGMENT]: t(
      "Manage static segments",
    ),
    [AutomationForgeElementType.RECOVER]: "Recover",
    [AutomationForgeElementType.SHOPPER_AGENT_MESSAGE]: t(
      "Start Agent Conversation",
    ),
    [AutomationForgeElementType.END]: t("End"),
    [AutomationForgeElementType.WAIT]: t("Wait"),
    [AutomationForgeElementType.WAIT_FOR_EVENT]: t("Wait for event"),
    [AutomationForgeElementType.BRANCH]: t("Branch"),
    [AutomationForgeElementType.UPSELL]: t("Upsell"),
    [AutomationForgeElementType.DOWNSELL]: t("Downsell"),
    [AutomationForgeElementType.OMS_ACTION]: t("Action"),
    [AutomationForgeElementType.OMS_CONDITION]: t("Condition"),
    [AutomationForgeElementType.OMS_CODE]: t("Code"),
    [AutomationForgeElementType.RCO_PARCEL_CONDITION]: t("Condition"),
    [AutomationForgeElementType.RCO_PARCEL]: t("Package"),
    [AutomationForgeElementType.CHECKOUT_EXPERIENCE]: t("Checkout experience"),
    [AutomationForgeElementType.BLOCK_CHECKOUT]: t("Block checkout"),
    [AutomationForgeElementType.RCO_SEGMENT]: t("Segment"),
    [AutomationForgeElementType.RCO_SPLIT]: t("A/B Test"),
    [AutomationForgeElementType.AB_TEST]: t("Weighted branch"),
    [AutomationForgeElementType.POST_PURCHASE_PAGE]: t("Post-purchase page"),
    [AutomationForgeElementType.POST_PURCHASE_SEGMENT]: t(
      "Post-purchase segment",
    ),
    [AutomationForgeElementType.POST_PURCHASE_SPLIT]: t("A/B Test"),
    [AutomationForgeElementType.TRACKING_PAGE]: t("Tracking page"),
    [AutomationForgeElementType.RULE_ACTIONS]: t("Actions"),
    [AutomationForgeElementType.RULE_CONDITIONS]: t("Conditions"),
    [AutomationForgeElementType.RULE_ACTIONS_V3]: t("Actions"),
    [AutomationForgeElementType.RULE_CONDITIONS_V3]: t("Conditions"),
    [AutomationForgeElementType.WORK_ORDER_WORK_STEP]: t("Work step"),
    [AutomationForgeElementType.WORK_ORDER_CONDITION]: t("Condition"),
    [AutomationForgeElementType.SUPPLIES_SHIPPING_CART_VALUE]: t("Cart value"),
    [AutomationForgeElementType.SUPPLIES_SHIPPING_FIRST_TIME_CUSTOMER]: t(
      "First supplies order",
    ),
    [AutomationForgeElementType.SUPPLIES_SHIPPING_AUTOMATED_REORDER]:
      t("Automated reorder"),
    [AutomationForgeElementType.SUPPLIES_SHIPPING_MERCHANT]: t("Merchant"),
    [AutomationForgeElementType.SUPPLIES_SHIPPING_CART_ITEMS]: t("Cart items"),
    [AutomationForgeElementType.SUPPLIES_SHIPPING_FAMILY_TAGS]: t(
      "Product family tags",
    ),
    [AutomationForgeElementType.SUPPLIES_SHIPPING_DESTINATION_PROVINCE]:
      t("Destination state"),
    [AutomationForgeElementType.SUPPLIES_SHIPPING_RATE]: t("Set shipping cost"),
    [AutomationForgeElementType.PROCESS_RETURN]: t("Process return"),
    [AutomationForgeElementType.CONCIERGE_BLOCK]: "Concierge",
    [AutomationForgeElementType.HSA_QUESTION_GROUP]: t("Question group"),
    [AutomationForgeElementType.HSA_CONDITION]: t("Condition"),
    [AutomationForgeElementType.HSA_KNOCKOUT]: t("Knockout"),
    [AutomationForgeElementType.HSA_CONTENT]: t("Content screen"),
    [AutomationForgeElementType.HSA_END]: t("Survey complete"),
  };
  return (
    <FormSidePanel
      bgColor={bgColor}
      className={flowEditorStyles.leftPanel}
      fixedWidth={296}
      side="left"
    >
      <Flex dir="column" gap="3xl" px="6xl" py="3xl">
        <Flex dir="column">
          <Text fontWeight="semibold">{title ?? t("Steps")}</Text>
          <Text fontSize="sm" textColor="secondary">
            {description ??
              t(
                "Create your automation by dragging and dropping steps into the map.",
              )}
          </Text>
        </Flex>
        {stepOptions.map((opt, i) => {
          return (
            <Flex dir="column" key={i}>
              {opt.name ? (
                <Text fontWeight="semibold">{opt.name}</Text>
              ) : undefined}
              {renderInvalidStepAlert &&
                renderInvalidStepAlert(invalidSteps, opt.steps, onGoToStep)}
              {opt.steps.map((v) => {
                const { Icon } = AutomationForgeDisplayInfo[v];
                const isDisabled =
                  disabledStepTypes?.some((d) => d.kind === v) ?? false;
                const disabledTooltip = disabledStepTypes?.find(
                  (d) => d.kind === v,
                )?.disabledTooltip;
                return (
                  <FormElementDrag disabled={isDisabled} key={v} type={v}>
                    <DraggableFormElement
                      disabled={isDisabled}
                      disabledTooltip={disabledTooltip}
                      Icon={Icon}
                      name={translatedNames[v]}
                    />
                  </FormElementDrag>
                );
              })}
            </Flex>
          );
        })}
      </Flex>
    </FormSidePanel>
  );
});
